// ─── Payslip font presets ─────────────────────────────────────────────────
// One source of truth used by the Salary Structure modal, Payslip_Generate
// Step 3, Payslip_Management edit modal, the HTML preview modals, and the
// jsPDF generators (both dAdmin and dSlip).
//
// Each preset carries:
//   key          - what's stored in the DB column `font`
//   label        - what's shown in the UI <select>
//   pdfFamily    - jsPDF built-in family name passed to doc.setFont(...)
//   pdfStyle     - jsPDF built-in style ("normal" | "bold" | "italic" | "bolditalic")
//   cssFamily    - CSS font-family stack for the HTML preview modals
//   cssWeight    - CSS font-weight (defaults to 400)
//   cssStyle     - CSS font-style ("normal" | "italic")
//
// PDF NOTE: jsPDF ships with only helvetica / times / courier built in, so
// Calibri / Arial / Sans-Serif / Poppins all map to "helvetica" in the PDF
// (their visual differences only show up in the HTML preview, which uses
// the cssFamily stack). Times New Roman maps to jsPDF's "times". Adding
// real TTF registrations for the missing faces would let the PDF render
// each one verbatim, but the dropdown options remain functional today.

export const PAYSLIP_FONTS = [
    {
        key: "helvetica",
        label: "Helvetica (default)",
        pdfFamily: "helvetica",
        pdfStyle: "normal",
        cssFamily: "Helvetica, Arial, sans-serif",
        cssWeight: 400,
        cssStyle: "normal",
    },
    {
        key: "helvetica_bold",
        label: "Helvetica Bold",
        pdfFamily: "helvetica",
        pdfStyle: "bold",
        cssFamily: "Helvetica, Arial, sans-serif",
        cssWeight: 700,
        cssStyle: "normal",
    },
    {
        key: "times",
        label: "Times Roman",
        pdfFamily: "times",
        pdfStyle: "normal",
        cssFamily: "'Times New Roman', Times, serif",
        cssWeight: 400,
        cssStyle: "normal",
    },
    {
        key: "times_italic",
        label: "Times Italic",
        pdfFamily: "times",
        pdfStyle: "italic",
        cssFamily: "'Times New Roman', Times, serif",
        cssWeight: 400,
        cssStyle: "italic",
    },
    {
        key: "courier",
        label: "Courier",
        pdfFamily: "courier",
        pdfStyle: "normal",
        cssFamily: "'Courier New', Courier, monospace",
        cssWeight: 400,
        cssStyle: "normal",
    },
    {
        key: "calibri",
        label: "Calibri",
        // jsPDF doesn't ship Calibri - fall back to helvetica in the PDF.
        // HTML preview shows the real Calibri when the user's OS has it.
        pdfFamily: "helvetica",
        pdfStyle: "normal",
        cssFamily: "Calibri, 'Segoe UI', Helvetica, Arial, sans-serif",
        cssWeight: 400,
        cssStyle: "normal",
    },
    {
        key: "times_new_roman",
        label: "Times New Roman",
        pdfFamily: "times",
        pdfStyle: "normal",
        cssFamily: "'Times New Roman', Times, serif",
        cssWeight: 400,
        cssStyle: "normal",
    },
    {
        key: "arial",
        label: "Arial",
        // jsPDF's helvetica is the conventional substitute for Arial.
        pdfFamily: "helvetica",
        pdfStyle: "normal",
        cssFamily: "Arial, Helvetica, sans-serif",
        cssWeight: 400,
        cssStyle: "normal",
    },
    {
        key: "sans_serif",
        label: "Sans-Serif",
        pdfFamily: "helvetica",
        pdfStyle: "normal",
        cssFamily: "sans-serif",
        cssWeight: 400,
        cssStyle: "normal",
    },
    {
        key: "poppins",
        label: "Poppins",
        // No jsPDF Poppins - falls back to helvetica in the PDF.
        // HTML preview renders true Poppins when the font is loaded
        // (e.g. via a project-level Google Fonts <link>).
        pdfFamily: "helvetica",
        pdfStyle: "normal",
        cssFamily: "Poppins, 'Segoe UI', Helvetica, Arial, sans-serif",
        cssWeight: 400,
        cssStyle: "normal",
    },
];

export const DEFAULT_FONT_KEY = "helvetica";

const FONT_MAP = (() => {
    const m = {};
    PAYSLIP_FONTS.forEach((f) => { m[f.key] = f; });
    return m;
})();

// Safe lookup - falls back to the default if the key is unknown or null.
// Useful while reading legacy rows that don't have a font column yet.
export function getFont(key) {
    return FONT_MAP[key] || FONT_MAP[DEFAULT_FONT_KEY];
}

// CSS style object suitable for spreading onto a preview root.
export function fontCssStyle(key) {
    const f = getFont(key);
    return {
        fontFamily: f.cssFamily,
        fontWeight: f.cssWeight,
        fontStyle: f.cssStyle,
    };
}
